import { CAST_PILOT_ASSISTANT } from '@cast/constants';
import pluralize from 'pluralize';

import {
  ActionsGroup,
  Button,
  DialogV2,
  Divider,
  Inset,
  makeDialog,
  MetadataChipV2,
  Stack,
  tokens,
  Typography,
  useCloseDialog,
} from '@cast/design-system';

import { raiseReportingMonetizationEvent } from '../analytics/reportingMonetizationEvents';
import { GreenCheckIcon } from '../_components/icons';
import { TrialListItemWithIcon } from '../_components/TrialListItemWithIcon';
import {
  formatReportingPlanPricePerMonth,
  formatReportingTrialEndShort,
  formatReportingVcpu,
  freeVcpuCapLabel,
} from '../reportingFormat';
import {
  getReportingExplainDialogVariant,
  recommendReportingPlan,
  type ReportingTrialFleet,
  SELF_SERVE_VCPU_CAP,
} from '../reportingPlans';
import { useReportingTrial } from '../useReportingTrial';

const INCLUDED_IN_TRIAL: Array<{ title: string; description: string }> = [
  {
    title: 'Full query history and higher-resolution reporting',
    description: 'Longer retention and finer-grained metrics across your fleet',
  },
  {
    title: 'Cost allocation and organization-wide reporting',
    description: 'Break down spend by team, namespace, workload, and more',
  },
  {
    title: 'Network, GPU, and storage cost reporting',
    description: 'Visibility beyond compute so every cost driver is covered',
  },
  {
    title: 'Reliability reporting and alerts',
    description: 'Catch issues early with reliability signals and alerts',
  },
  {
    title: `${CAST_PILOT_ASSISTANT} and Prometheus API access`,
    description: 'Query and automate with the tools your team already uses',
  },
  {
    title: 'Standard support',
    description: 'Help when you need it',
  },
];

export type ReportingTrialExplainDialogProps = {
  fleet?: ReportingTrialFleet;
  onViewPlans?: () => void;
  onContactSales?: () => void;
};

const SectionLabel = ({ children }: { children: string }) => (
  <Typography
    variant="L12B"
    color={tokens.theme.fg.structural.secondary}
    sx={{ textTransform: 'uppercase', letterSpacing: '1px' }}
  >
    {children}
  </Typography>
);

/** After-trial copy by fleet size — Figma 27:61814 / 27:63189 / 27:63321. */
const AfterTrialCopy = ({
  avgVcpu,
  trialEndsOn,
}: {
  avgVcpu?: number;
  trialEndsOn?: string;
}) => {
  const variant = getReportingExplainDialogVariant(avgVcpu ?? Number.NaN);

  if (variant === 'enterprise') {
    return (
      <>
        <Typography variant="P14R">
          Your fleet exceeds the {formatReportingVcpu(SELF_SERVE_VCPU_CAP)}-vCPU
          self-service limit. Contact sales for a plan that covers all your
          clusters.
        </Typography>
        <Typography variant="P14R">
          You can also choose a self-service plan for selected clusters.
          {trialEndsOn ? ` Your trial ends on ${trialEndsOn}.` : ''}
        </Typography>
        <Typography variant="P14R">
          Arrange a plan before then to avoid a pause in reporting collection.
        </Typography>
      </>
    );
  }

  if (variant === 'free') {
    return (
      <>
        <Typography variant="P14R">
          Your current fleet fits the Free plan. If it stays within{' '}
          {freeVcpuCapLabel()} average vCPUs, all your clusters will continue
          with basic reporting automatically.
        </Typography>
        <Typography variant="P14R">
          Free includes 30-day history and hourly resolution.{' '}
          {CAST_PILOT_ASSISTANT} and other paid capabilities are not included.
          Choose a paid plan to keep them.
        </Typography>
      </>
    );
  }

  // Plan recommendation requires known fleet usage. Unknown (absent) fleet
  // stats resolve to the 'free' variant above, so only this recommendation
  // block is gated on `avgVcpu` being present.
  if (avgVcpu == null) {
    return null;
  }

  const recommended = recommendReportingPlan(avgVcpu);
  const price = formatReportingPlanPricePerMonth(recommended.priceLabel);

  return (
    <>
      <Typography variant="P14R">
        {recommended.name} at {price} is the smallest plan that covers your
        current fleet, with capacity for up to{' '}
        {recommended.vcpuCap != null
          ? formatReportingVcpu(recommended.vcpuCap)
          : undefined}{' '}
        average vCPUs.
      </Typography>
      <Typography variant="P14R">
        You can also continue on Free by selecting clusters totaling up to{' '}
        {freeVcpuCapLabel()} average vCPUs, with 30-day history and hourly
        resolution.
      </Typography>
      <Typography variant="P14R">
        {trialEndsOn
          ? `Choose a plan before ${trialEndsOn} to avoid a pause in reporting collection.`
          : 'Choose a plan before the trial ends to avoid a pause in reporting collection.'}
      </Typography>
    </>
  );
};

const ReportingTrialExplainDialogContent = ({
  fleet,
  onViewPlans,
  onContactSales,
}: ReportingTrialExplainDialogProps) => {
  const closeDialog = useCloseDialog();
  const { expiresInDays, expiresAt, state } = useReportingTrial();

  const avgVcpu = fleet?.avgProvisionedVcpu;
  const variant = getReportingExplainDialogVariant(avgVcpu ?? Number.NaN);
  const isEnterprise = variant === 'enterprise';
  const trialEndsOn = formatReportingTrialEndShort(expiresAt);
  const showFleetChips =
    (avgVcpu ?? 0) > 0 ||
    fleet?.clusterCount != null ||
    fleet?.nodeCount != null;

  const title = (() => {
    switch (state) {
      case 'pre-trial':
        return 'Your 30-day reporting trial';
      case 'post-trial':
      case 'grace-period':
        return 'Your trial has ended';
      case 'active':
        if (expiresInDays === 0) {
          return 'Trial expires today';
        }
        if (Number.isFinite(expiresInDays)) {
          return `${expiresInDays} ${pluralize(
            'day',
            expiresInDays
          )} left of your trial`;
        }
        return 'Your reporting trial';
      default:
        return 'Your reporting trial';
    }
  })();

  const handleViewPlans = () => {
    raiseReportingMonetizationEvent('reporting_offer_viewed', {
      source: 'trial_explain_dialog',
    });
    onViewPlans?.();
    closeDialog();
  };

  const handleContactSales = () => {
    raiseReportingMonetizationEvent('reporting_plan_selected', {
      source: 'trial_explain_dialog',
      selected_plan: 'enterprise',
    });
    onContactSales?.();
    closeDialog();
  };

  return (
    <DialogV2
      testId="reporting-trial-explain-dialog"
      title={title}
      footerDivider
      endActions={
        isEnterprise ? (
          <ActionsGroup>
            <Button
              variant="secondary"
              testId="reporting-trial-view-plans"
              onClick={handleViewPlans}
            >
              View plans
            </Button>
            <Button
              variant="primary"
              testId="reporting-trial-contact-sales"
              onClick={handleContactSales}
            >
              Contact sales
            </Button>
          </ActionsGroup>
        ) : (
          <Button
            variant="primary"
            testId="reporting-trial-view-plans"
            onClick={handleViewPlans}
          >
            View plans
          </Button>
        )
      }
    >
      <Inset pb={8} mb={24}>
        <Stack gap={16}>
          {showFleetChips ? (
            <Stack direction="row" gap={8} flexWrap="wrap">
              {/* Figma chip order: clusters → nodes → vCPU */}
              {fleet?.clusterCount != null ? (
                <MetadataChipV2
                  label={`${fleet.clusterCount} ${pluralize(
                    'cluster',
                    fleet.clusterCount
                  )}`}
                  size="xs"
                  variant="strong"
                />
              ) : null}
              {fleet?.nodeCount != null ? (
                <MetadataChipV2
                  label={`${fleet.nodeCount} ${pluralize(
                    'node',
                    fleet.nodeCount
                  )}`}
                  size="xs"
                  variant="strong"
                />
              ) : null}
              {avgVcpu != null && avgVcpu > 0 ? (
                <MetadataChipV2
                  label={`${Math.round(avgVcpu)} vCPU`}
                  size="xs"
                  variant="strong"
                />
              ) : null}
            </Stack>
          ) : null}

          <Typography variant="P14R">
            {state === 'pre-trial'
              ? 'Connecting a cluster starts your trial. Explore all paid reporting capabilities — no card required, no automatic charge.'
              : 'Explore all paid reporting capabilities during your trial. No automatic charge.'}
          </Typography>

          <Divider />

          <Stack gap={12}>
            <SectionLabel>Included in your trial</SectionLabel>
            <Stack
              gap={8}
              component="ul"
              sx={{ m: 0, p: 0, listStyle: 'none' }}
            >
              {INCLUDED_IN_TRIAL.map((item) => (
                <TrialListItemWithIcon
                  key={item.title}
                  icon={<GreenCheckIcon />}
                  text={item.title}
                  description={item.description}
                />
              ))}
            </Stack>
          </Stack>

          <Divider />

          <Stack gap={8}>
            <SectionLabel>After your trial</SectionLabel>
            <AfterTrialCopy avgVcpu={avgVcpu} trialEndsOn={trialEndsOn} />
          </Stack>
        </Stack>
      </Inset>
    </DialogV2>
  );
};

export const useReportingTrialExplainDialog = makeDialog(
  ReportingTrialExplainDialogContent
);
